import { createApi } from '@reduxjs/toolkit/query/react';

import { writeStoredAuth } from '@core/auth/tokenStorage';
import type { User } from '@/types/user';

import { rootApiBaseUrl } from '../Api';
import { makeBaseQuery } from '../baseQuery';
import type { RootState } from '../rootReducer';

import { setUser } from './authSlice';
import type {
  AuthSession,
  EmailVerificationInfo,
  LoginArgs,
  MeProfile,
  MeResult,
  OtpLoginArgs,
  RegisterPayload,
  ResendVerificationResult,
  ResetPasswordArgs,
  UpdateMeArgs
} from './authTypes';

/**
 * Every backend response is `{ success, message, data }`. The envelope is stripped here so
 * no component ever writes `res.data.data`. Normalizers are module-scope, take `unknown`
 * and are TOTAL — they never throw and never return `undefined`, because a normalizer that
 * throws takes out the route's error boundary instead of showing the query's error state.
 */
const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

const envelope = (response: unknown): Record<string, unknown> => asRecord(asRecord(response).data);

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asStoredPath = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

function normalizeUser(value: unknown): User | null {
  const raw = asRecord(value);
  if (typeof raw.id !== 'number' || typeof raw.email !== 'string') return null;
  return {
    id: raw.id,
    name: asString(raw.name),
    email: raw.email,
    mobile: asString(raw.mobile),
    role: raw.role as User['role'],
    emailVerified: typeof raw.emailVerified === 'boolean' ? raw.emailVerified : undefined,
    avatarPath: asStoredPath(raw.avatarPath),
    coverPath: asStoredPath(raw.coverPath)
  };
}

function normalizeEmailVerification(value: unknown): EmailVerificationInfo | undefined {
  const raw = asRecord(value);
  if (typeof raw.status !== 'string') return undefined;
  return {
    status: raw.status as EmailVerificationInfo['status'],
    message: asString(raw.message)
  };
}

/** A session with no token is unusable, so an empty token is the signal the caller checks. */
function normalizeSession(response: unknown): AuthSession {
  const data = envelope(response);
  const user = normalizeUser(data.user);
  return {
    token: asString(data.token),
    user: user ?? ({ id: 0, name: '', email: '', mobile: '', role: 'buyer' } as User),
    emailVerification: normalizeEmailVerification(data.emailVerification)
  };
}

function normalizeVerifiedUser(response: unknown): User | null {
  return normalizeUser(envelope(response).user);
}

function normalizeResetToken(response: unknown): string {
  return asString(envelope(response).resetToken);
}

function normalizeResend(response: unknown): ResendVerificationResult {
  const root = asRecord(response);
  return {
    message: asString(root.message, 'Verification email sent.'),
    emailVerification: normalizeEmailVerification(envelope(response).emailVerification) ?? null
  };
}

function normalizeMe(response: unknown): MeResult {
  const data = envelope(response);
  const raw = asRecord(data.user);
  const user: MeProfile | null =
    typeof raw.id === 'number'
      ? {
          id: raw.id,
          name: asString(raw.name),
          email: asString(raw.email),
          mobile: asString(raw.mobile),
          role: raw.role as MeProfile['role'],
          emailVerified: raw.emailVerified === true,
          createdAt: asString(raw.createdAt),
          addressLine: asString(raw.addressLine),
          pincode: asString(raw.pincode),
          state: asString(raw.state),
          district: asString(raw.district),
          taluk: asString(raw.taluk),
          location: asString(raw.location),
          avatarPath: asStoredPath(raw.avatarPath),
          coverPath: asStoredPath(raw.coverPath)
        }
      : null;
  return { user, profile: data.profile === null ? null : asRecord(data.profile) };
}

type Photos = Pick<User, 'avatarPath' | 'coverPath'>;
type SessionFields = Partial<Pick<User, 'name' | 'email' | 'mobile'>> & Photos;

// NOTE: setUser alone is lost on reload; the stored session must follow it.
function syncSessionUser(
  fields: SessionFields,
  dispatch: (action: ReturnType<typeof setUser>) => unknown,
  getState: () => unknown
): void {
  const { token, user } = (getState() as RootState).auth;
  if (!user) return;
  const changed = (Object.keys(fields) as (keyof SessionFields)[]).some(
    (k) => user[k] !== fields[k]
  );
  if (!changed) return;
  const next: User = { ...user, ...fields };
  dispatch(setUser(next));
  writeStoredAuth({ token, user: next });
}

const photoFields = (user: Photos): Photos => ({
  avatarPath: user.avatarPath ?? null,
  coverPath: user.coverPath ?? null
});

const photoForm = (file: File): FormData => {
  const body = new FormData();
  body.append('photo', file);
  return body;
};

async function onPhotoSaved(
  _arg: unknown,
  api: {
    dispatch: (action: ReturnType<typeof setUser>) => unknown;
    getState: () => unknown;
    queryFulfilled: Promise<{ data: User | null }>;
  }
): Promise<void> {
  try {
    const { data } = await api.queryFulfilled;
    if (data) syncSessionUser(photoFields(data), api.dispatch, api.getState);
  } catch {
    /* the mutation's own error reaches the caller through unwrap() */
  }
}

/**
 * `/auth/*` and `/user/*` are mounted at the server root, NOT under `/api` — pointing this
 * service at `apiBaseUrl` is a 404 on every call.
 */
export const authServiceApi = createApi({
  reducerPath: 'authServiceApi',
  baseQuery: makeBaseQuery(rootApiBaseUrl),
  tagTypes: ['Me'],
  endpoints: (builder) => ({
    register: builder.mutation<AuthSession, RegisterPayload>({
      query: (body) => ({ url: 'auth/register', method: 'POST', body }),
      transformResponse: (response: unknown) => normalizeSession(response)
    }),
    login: builder.mutation<AuthSession, LoginArgs>({
      query: (body) => ({ url: 'auth/login', method: 'POST', body }),
      transformResponse: (response: unknown) => normalizeSession(response)
    }),
    requestLoginOtp: builder.mutation<void, { email: string }>({
      query: (body) => ({ url: 'auth/request-login-otp', method: 'POST', body })
    }),
    otpLogin: builder.mutation<AuthSession, OtpLoginArgs>({
      query: (body) => ({ url: 'auth/otp-login', method: 'POST', body }),
      transformResponse: (response: unknown) => normalizeSession(response)
    }),
    verifyEmailOtp: builder.mutation<User | null, { otp: string }>({
      query: (body) => ({ url: 'auth/verify-email', method: 'POST', body }),
      transformResponse: (response: unknown) => normalizeVerifiedUser(response),
      invalidatesTags: ['Me']
    }),
    resendEmailVerification: builder.mutation<ResendVerificationResult, void>({
      query: () => ({ url: 'auth/resend-email-verification', method: 'POST', body: {} }),
      transformResponse: (response: unknown) => normalizeResend(response)
    }),
    // The backend returns the raw reset token in the body; the reset screen consumes it.
    forgotPassword: builder.mutation<string, { email: string }>({
      query: (body) => ({ url: 'auth/forgot-password', method: 'POST', body }),
      transformResponse: (response: unknown) => normalizeResetToken(response)
    }),
    resetPassword: builder.mutation<void, ResetPasswordArgs>({
      query: (body) => ({ url: 'auth/reset-password', method: 'POST', body })
    }),
    getMe: builder.query<MeResult, void>({
      query: () => ({ url: 'user/me' }),
      transformResponse: (response: unknown) => normalizeMe(response),
      providesTags: ['Me'],
      onQueryStarted: async (_arg, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.user) {
            const { name, email, mobile } = data.user;
            syncSessionUser({ name, email, mobile, ...photoFields(data.user) }, dispatch, getState);
          }
        } catch {
          /* the query's own error state reports it */
        }
      }
    }),
    updateMe: builder.mutation<void, UpdateMeArgs>({
      query: (body) => ({ url: 'user/me', method: 'PUT', body }),
      invalidatesTags: ['Me']
    }),
    uploadMyAvatar: builder.mutation<User | null, File>({
      query: (file) => ({ url: 'user/me/avatar', method: 'POST', body: photoForm(file) }),
      transformResponse: (response: unknown) => normalizeVerifiedUser(response),
      invalidatesTags: ['Me'],
      onQueryStarted: onPhotoSaved
    }),
    removeMyAvatar: builder.mutation<User | null, void>({
      query: () => ({ url: 'user/me/avatar', method: 'DELETE' }),
      transformResponse: (response: unknown) => normalizeVerifiedUser(response),
      invalidatesTags: ['Me'],
      onQueryStarted: onPhotoSaved
    }),
    uploadMyCover: builder.mutation<User | null, File>({
      query: (file) => ({ url: 'user/me/cover', method: 'POST', body: photoForm(file) }),
      transformResponse: (response: unknown) => normalizeVerifiedUser(response),
      invalidatesTags: ['Me'],
      onQueryStarted: onPhotoSaved
    }),
    removeMyCover: builder.mutation<User | null, void>({
      query: () => ({ url: 'user/me/cover', method: 'DELETE' }),
      transformResponse: (response: unknown) => normalizeVerifiedUser(response),
      invalidatesTags: ['Me'],
      onQueryStarted: onPhotoSaved
    })
  })
});

export const {
  useRegisterMutation,
  useLoginMutation,
  useRequestLoginOtpMutation,
  useOtpLoginMutation,
  useVerifyEmailOtpMutation,
  useResendEmailVerificationMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useGetMeQuery,
  useUpdateMeMutation,
  useUploadMyAvatarMutation,
  useRemoveMyAvatarMutation,
  useUploadMyCoverMutation,
  useRemoveMyCoverMutation
} = authServiceApi;
